import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import ErpTemplate from '../../../components/ErpTemplate';
import { FaArrowLeft, FaFileInvoiceDollar, FaSearch, FaSync } from 'react-icons/fa';
import api from '../../../services/api';
import { moeda } from '../../../util/util';
import { OrigemMoeda } from '../../../components/ValorComOrigem';
import { toast } from 'react-toastify';
import { useSetFullBreadcrumbTrail } from '../../../context/BreadcrumbContext';
import '../../../css/financeiro-parcela-pagar.css';

// Tela "Faturamento" — a fila da Simone: orçamentos que o operador de Evento
// enviou para faturar. Segue o shell padrão do financeiro (breadcrumb +
// .fin-pp__head + abas + região com tabela .fin-pp__tbl — modelo:
// financeiroParcelaPagar). Três faixas derivadas do backend:
// aguardando (nada gerado) → em andamento (documento previsto) → finalizado.
const FAIXAS = [
    { key: 'aguardando', label: 'Aguardando', sub: 'Enviados pelo operador, nenhum documento gerado ainda.' },
    { key: 'em_andamento', label: 'Em andamento', sub: 'Já tem documento previsto, aguardando o número da prefeitura.' },
    { key: 'finalizado', label: 'Finalizado', sub: 'Todos os documentos vigentes do evento já foram emitidos.' },
];

// "2026-08-27 14:34:41" → "27/08/2026 14:34" (sem depender de moment/timezone)
function dataHora(valor) {
    var texto = String(valor || '');
    if (texto.length < 10) return '—';
    var dia = texto.slice(8, 10) + '/' + texto.slice(5, 7) + '/' + texto.slice(0, 4);
    return texto.length >= 16 ? dia + ' ' + texto.slice(11, 16) : dia;
}

// Documento previsto ainda é rascunho; emitido já valeu.
function toneDocumento(situacao) {
    var s = String(situacao || '').toLowerCase();
    if (s === 'emitido') return ' is-ok';
    if (s === 'previsto') return ' is-warn';
    return '';
}

export default function PainelFaturamento() {
    const history = useHistory();
    const token = localStorage.getItem('token');
    const setFullTrail = useSetFullBreadcrumbTrail();

    const [faixa, setFaixa] = useState('aguardando');
    const [busca, setBusca] = useState('');            // o que está digitado
    const [buscaAplicada, setBuscaAplicada] = useState(''); // o que foi enviado à API
    const [orcamentos, setOrcamentos] = useState([]);
    const [contadores, setContadores] = useState({});
    const [carregando, setCarregando] = useState(false);

    useEffect(() => {
        setFullTrail([
            { path: '/financeiro', label: 'Financeiro' },
            { path: '/financeiroFaturamento', label: 'Faturamento' },
        ]);
        // eslint-disable-next-line
    }, []);

    // Trocar de aba é mais rápido que a resposta da API: o contador de requisição
    // descarta o retorno atrasado de uma faixa que não é mais a visível.
    const requisicaoAtual = useRef(0);

    const buscar = useCallback(() => {
        requisicaoAtual.current += 1;
        const daVez = requisicaoAtual.current;
        setCarregando(true);

        api({
            method: 'get',
            action: '/faturamento',
            token: token,
            params: { faixa: faixa, busca: buscaAplicada || undefined },
        })
            .then(function (response) {
                if (daVez !== requisicaoAtual.current) return;
                const data = response.data || {};
                setOrcamentos(data.orcamentos || []);
                setContadores(data.contadores || {});
            })
            .catch(function () {
                if (daVez !== requisicaoAtual.current) return;
                setOrcamentos([]);
                toast.error('Erro ao carregar o Faturamento.');
            })
            .finally(function () {
                if (daVez === requisicaoAtual.current) setCarregando(false);
            });
    }, [faixa, buscaAplicada, token]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    const aplicarBusca = useCallback(() => {
        setBuscaAplicada(busca.trim());
    }, [busca]);

    const faixaAtual = FAIXAS.filter(function (f) { return f.key === faixa; })[0] || FAIXAS[0];
    const qtdFaixa = contadores[faixa] || 0;

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">

                {/* Header */}
                <div className="fin-pp__head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                            onClick={function () { history.push('/financeiro'); }} title="Voltar ao Financeiro">
                            <FaArrowLeft size={13} />
                        </button>
                        <div>
                            <h1 className="fin-pp__title">Faturamento</h1>
                            <p className="fin-pp__subtitle">
                                Conferência dos documentos do evento antes do Contas a Receber — ND e NF por fornecedor.
                            </p>
                        </div>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn" onClick={buscar} disabled={carregando}>
                            <FaSync size={14} /> {carregando ? 'Atualizando…' : 'Atualizar'}
                        </button>
                    </div>
                </div>

                {/* Busca */}
                <div className="fin-pp__card" style={{ padding: '14px 16px', marginBottom: 14 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <div className="fin-pp__search-field" style={{ flex: '1 1 260px' }}>
                            <FaSearch className="fin-pp__search-ic" size={16} />
                            <input
                                type="text"
                                className="fin-pp__input"
                                placeholder="Buscar por evento, código ou orçamento…"
                                value={busca}
                                onChange={function (e) { setBusca(e.target.value); }}
                                onKeyDown={function (e) { if (e.key === 'Enter') aplicarBusca(); }}
                            />
                        </div>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--tall"
                            onClick={aplicarBusca} disabled={carregando}>
                            <FaSearch size={14} /> {carregando ? 'Carregando…' : 'Buscar'}
                        </button>
                        {buscaAplicada !== '' && (
                            <button type="button" className="fin-pp__btn fin-pp__btn--tall"
                                onClick={function () { setBusca(''); setBuscaAplicada(''); }}>
                                Limpar
                            </button>
                        )}
                    </div>
                </div>

                {/* Abas — uma por faixa, com o contador que o backend devolve */}
                <div className="fin-pp__tabs mb-3">
                    {FAIXAS.map(function (f) {
                        var ativa = faixa === f.key;
                        var qtd = contadores[f.key] || 0;
                        return (
                            <button key={f.key} type="button" onClick={function () { setFaixa(f.key); }}
                                className={'fin-pp__tab' + (ativa ? ' is-active' : '')}>
                                {f.label}
                                {qtd > 0 && <span className="fin-pp__seg-count">{qtd}</span>}
                            </button>
                        );
                    })}
                </div>

                {/* Região de listagem */}
                <section className="fin-pp__region">
                    <div className="fin-pp__cphead">
                        <span className="fin-pp__cphead-ic"><FaFileInvoiceDollar size={16} /></span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                            <h2 className="fin-pp__cphead-title">{faixaAtual.label}</h2>
                            <p className="fin-pp__cphead-sub">{faixaAtual.sub}</p>
                        </div>
                        <span className="fin-pp__cphead-pill">
                            {qtdFaixa} orçamento{qtdFaixa !== 1 ? 's' : ''}
                        </span>
                    </div>

                    <div className="fin-pp__region-body">
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl fin-pp__tbl--wide">
                                    <thead>
                                        <tr>
                                            <th>Evento</th>
                                            <th style={{ width: 110 }}>Código</th>
                                            <th style={{ width: 240 }}>Cliente</th>
                                            <th style={{ width: 220 }}>Orçamento</th>
                                            <th style={{ width: 150 }}>Enviado em</th>
                                            <th style={{ width: 280 }}>Documentos</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {carregando && (
                                            <tr><td colSpan={6} className="fin-pp__empty">Carregando…</td></tr>
                                        )}
                                        {!carregando && orcamentos.length === 0 && (
                                            <tr><td colSpan={6} className="fin-pp__empty">Nenhum orçamento nesta faixa.</td></tr>
                                        )}
                                        {!carregando && orcamentos.map(function (o) {
                                            var documentos = o.documentos || [];
                                            return (
                                                <tr key={o.orcamento_id}
                                                    style={{ cursor: 'pointer' }}
                                                    title="Abrir a conferência do faturamento"
                                                    onClick={function () { history.push('/financeiroFaturamento/' + o.orcamento_id); }}>
                                                    <td className="fin-pp__cell-strong fin-pp__ellipsis">{o.evento_nome || '—'}</td>
                                                    <td className="fin-pp__cell-muted">{o.evento_codigo || '—'}</td>
                                                    <td className="fin-pp__ellipsis">{o.cliente_nome || '—'}</td>
                                                    <td className="fin-pp__ellipsis">{o.orcamento_titulo || '—'}</td>
                                                    <td className="fin-pp__cell-muted">{dataHora(o.enviado_em)}</td>
                                                    <td>
                                                        {documentos.length === 0 ? (
                                                            <span className="fin-pp__cell-muted">—</span>
                                                        ) : (
                                                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                                {documentos.map(function (d) {
                                                                    return (
                                                                        <span key={d.id} className={'fin-pp__minitag' + toneDocumento(d.situacao)}>
                                                                            {String(d.tipo || '').toUpperCase()} {d.numero || 's/nº'} · R$ {moeda(d.valor)}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                        <OrigemMoeda origem={o.origem_moeda} />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </section>

            </div>
        </ErpTemplate>
    );
}
